/**
 * ClientDetail — one client's whole configuration.
 *
 * Four things are edited here and each one has a way of going wrong quietly:
 *
 * 1. **Identity.** `company_id` is the authoritative workspace key and is not derivable from
 *    the S3 prefix (CONTRACT §2). The lender name is repeated at the top of the page because
 *    `credgenics` and `credgenics-genai` look identical at a glance and are ICICI and HDB.
 *
 * 2. **`field_map`.** Every client's schema differs and missing keys are legal (CONTRACT §4) —
 *    a client simply lacks that concept. The one rule that is not a preference: `key` is the
 *    borrower/account identity and is NEVER the phone number, so that combination is refused
 *    here as well as in the API.
 *
 * 3. **`link_ttl_days`.** Hard maximum 7 (CONTRACT §1), enforced in this UI and not only on
 *    the server, because an operator who types 30 and watches it save should not find out a
 *    week later that the client's links died on day 7. Lowering it is real; raising it past 7
 *    is not a thing that exists.
 *
 * 4. **The column policy.** Delegated to ColumnPolicyEditor, which owns the three tiers and
 *    the warnings.
 *
 * Client fields are a draft with an explicit save. Recipients are not: they write immediately,
 * and the card says so, because a half-saved recipient list is worse than an obvious one.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Badge, { ActiveBadge, PolicyBadge } from '@/components/Badge';
import Button from '@/components/Button';
import ColumnPolicyEditor, { callContent, isAlwaysNever } from '@/components/ColumnPolicyEditor';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import { Field, FieldRow, Input, Switch } from '@/components/Field';
import { Card, PageHeader } from '@/components/Layout';
import RecipientEditor from '@/components/RecipientEditor';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { api, errorMessage } from '@/lib/api';
import { formatCount, istDateTime, pluralise } from '@/lib/format';
import { MAX_LINK_TTL_DAYS, type Client, type ClientPatch, type ColumnPolicy } from '@/lib/types';

/** Fields the API adds beyond the wire type (services/api/clients.py :: public_client). */
interface ClientRow extends Client {
  seeded_only?: boolean;
  rank_count?: number;
  /** Seed `_defects` plus any `notes` — known problems with this client's source export. */
  notes?: string[];
  connected_values?: string[];
  source_date_format?: string;
  updated_by?: string;
}

// ---------------------------------------------------------------------------
// field_map roles
// ---------------------------------------------------------------------------

interface RoleSpec {
  role: string;
  label: string;
  help: string;
  /** A literal value rather than a source column name. */
  literal?: boolean;
}

const FIELD_ROLES: readonly RoleSpec[] = [
  {
    role: 'key',
    label: 'Borrower key',
    help: 'The account identity the borrower rollup groups on — one output row per distinct value. Never the phone number.',
  },
  {
    role: 'key_strip_prefix',
    label: 'Strip from key',
    help: 'A literal to remove from the front of the key, not a column name. Credgenics ships loan_id with a leading backtick on every row.',
    literal: true,
  },
  { role: 'phone', label: 'Phone', help: 'Rendered as last-4 everywhere in this console (CONTRACT §8.6).' },
  { role: 'name', label: 'Borrower name', help: 'Masked in every console surface and every log line.' },
  { role: 'language', label: 'Language', help: 'Carried through to the rollup as-is.' },
  {
    role: 'timestamp',
    label: 'Call timestamp',
    help: 'When the call was triggered. Drives first attempt, last attempt and the winning-call detail fields.',
  },
  { role: 'date', label: 'Call date', help: 'Date-only column, for sources that split date from time.' },
  {
    role: 'disposition',
    label: 'Disposition',
    help: 'The contact outcome. Ranked verbatim — case-folded on match, never spell-corrected.',
  },
  { role: 'sub_disposition', label: 'Sub-disposition', help: 'Secondary outcome, where the source has one.' },
  { role: 'disposition_category', label: 'Disposition category', help: 'Grouping column, where the source has one.' },
  { role: 'call_status', label: 'Call status', help: 'Connection status — distinct from the outcome of the conversation.' },
  { role: 'duration', label: 'Call duration', help: 'Summed into talk time on the rollup.' },
  {
    role: 'attempts',
    label: 'Attempts',
    help: 'Leave unmapped when the source has no attempt count — the rollup derives attempts by counting calls per key.',
  },
  {
    role: 'recording',
    label: 'Recording',
    help: 'Rewritten to a /r/{token} resolver link on delivery. A raw blob URL is never put in a delivered file (CONTRACT §1).',
  },
  { role: 'flow', label: 'Flow', help: 'Schedules can filter on this.' },
  { role: 'campaign', label: 'Campaign', help: 'Schedules can pattern-match on this.' },
  { role: 'company_id', label: 'company_id column', help: 'The workspace key as it appears inside the export.' },
  {
    role: 'contact_outcome',
    label: 'Contact outcome',
    help: 'Feeds the right-party gate together with reached_values — fusion uses Borrower_Reached / Self.',
  },
  { role: 'ptp_date', label: 'PTP date', help: 'Promise-to-pay date on the winning call.' },
  { role: 'ptp_amount', label: 'PTP amount', help: 'Promise-to-pay amount on the winning call.' },
  { role: 'committed_amount', label: 'Committed amount', help: 'Fusion’s equivalent of a PTP amount.' },
  { role: 'commitment_type', label: 'Commitment type', help: 'Full outstanding, lump sum, tranches.' },
  { role: 'callback_date', label: 'Callback date', help: 'When the borrower asked to be called back.' },
  {
    role: 'rpc',
    label: 'Right-party verified',
    help: 'The strongest right-party signal. Without it the gate falls back to contact_outcome, then to a ranked outcome, and gates closed if neither is present.',
  },
];

const KNOWN_ROLES = new Set(FIELD_ROLES.map((r) => r.role));

interface RoleRow extends RoleSpec {
  value: string;
  custom: boolean;
}

// ---------------------------------------------------------------------------
// Draft
// ---------------------------------------------------------------------------

/** The subset of a client this screen may change. Everything else is read-only here. */
const EDITABLE_FIELDS = [
  'name',
  's3_prefix',
  'company_id',
  'company_name',
  'field_map',
  'column_policy',
  'link_ttl_days',
  'retention_days',
  'active',
] as const;

type EditableField = (typeof EDITABLE_FIELDS)[number];

const FIELD_LABEL: Record<EditableField, string> = {
  name: 'display name',
  s3_prefix: 'source prefix',
  company_id: 'company_id',
  company_name: 'end lender',
  field_map: 'field map',
  column_policy: 'column policy',
  link_ttl_days: 'link life',
  retention_days: 'file retention',
  active: 'active flag',
};

/** The page's sections, in source order. The index in the header anchors to these ids. */
const SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'identity', label: 'Identity' },
  { id: 'links', label: 'Links' },
  { id: 'fieldmap', label: 'Field map' },
  { id: 'policy', label: 'Policy' },
  { id: 'ranking', label: 'Ranking' },
  { id: 'recipients', label: 'Recipients' },
];

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export default function ClientDetail() {
  const params = useParams<{ id: string }>();
  const clientId = params.id ?? '';
  const toast = useToast();

  const [saved, setSaved] = useState<ClientRow | null>(null);
  const [draft, setDraft] = useState<ClientRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [newRole, setNewRole] = useState('');
  /**
   * The link-life box keeps its own text while it is being edited. Binding the input straight
   * to the clamped number makes clearing it snap to 1, so typing "5" over a "7" goes
   * 7 → 1 → 15 → 7 and the operator never gets to 5.
   */
  const [ttlText, setTtlText] = useState<string>(String(MAX_LINK_TTL_DAYS));
  const [ttlClamped, setTtlClamped] = useState(false);
  /**
   * Field map and column policy are onboarding surfaces: configured once, then scrolled
   * past forever on the way to Recipients. They collapse once they hold a working
   * configuration and open on their own when they carry a problem. null = not decided by
   * a human yet, so the derived default below applies.
   */
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [openFieldMap, setOpenFieldMap] = useState<boolean | null>(null);
  const [openPolicy, setOpenPolicy] = useState<boolean | null>(null);
  const [recipientCounts, setRecipientCounts] = useState<{
    total: number;
    active: number;
    deliverableTo: number;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!clientId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const client = (await api.clients.get(clientId)) as ClientRow;
      setSaved(client);
      setDraft(client);
      setTtlText(String(client.link_ttl_days));
      setTtlClamped(false);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRecipientCounts = useCallback(
    (counts: { total: number; active: number; deliverableTo: number }) => setRecipientCounts(counts),
    [],
  );

  const changed = useMemo<EditableField[]>(() => {
    if (!saved || !draft) return [];
    return EDITABLE_FIELDS.filter((key) => !sameValue(saved[key], draft[key]));
  }, [saved, draft]);

  const patch = <K extends keyof ClientRow>(key: K, value: ClientRow[K]): void => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  // ------------------------------------------------------------------ field map

  const roleRows = useMemo<RoleRow[]>(() => {
    const map = draft?.field_map ?? {};
    const known = FIELD_ROLES.map<RoleRow>((spec) => ({
      ...spec,
      value: map[spec.role] ?? '',
      custom: false,
    }));
    const extras = Object.keys(map)
      .filter((role) => !KNOWN_ROLES.has(role) && !role.startsWith('_'))
      .sort()
      .map<RoleRow>((role) => ({
        role,
        label: role,
        help: 'Custom role. Readers that do not know it ignore it; nothing breaks, nothing happens.',
        value: map[role] ?? '',
        custom: true,
      }));
    return [...known, ...extras];
  }, [draft?.field_map]);

  const setRole = (role: string, value: string): void => {
    setDraft((current) => {
      if (!current) return current;
      const next = { ...(current.field_map ?? {}) };
      // key_strip_prefix is a literal — a leading backtick must survive verbatim, so it is the
      // one role whose value is not trimmed.
      const spec = FIELD_ROLES.find((r) => r.role === role);
      const cleaned = spec?.literal ? value : value.trim();
      if (cleaned === '') delete next[role];
      else next[role] = cleaned;
      return { ...current, field_map: next };
    });
  };

  const addRole = (): void => {
    const role = newRole.trim();
    if (!role || !draft) return;
    if (KNOWN_ROLES.has(role) || Object.prototype.hasOwnProperty.call(draft.field_map ?? {}, role)) {
      toast.info(`${role} is already in the list.`);
      setNewRole('');
      return;
    }
    patch('field_map', { ...(draft.field_map ?? {}), [role]: '' });
    setNewRole('');
  };

  const keyColumn = (draft?.field_map?.key ?? '').trim();
  const phoneColumn = (draft?.field_map?.phone ?? '').trim();
  const keyIsPhone =
    keyColumn !== '' && phoneColumn !== '' && keyColumn.toLowerCase() === phoneColumn.toLowerCase();

  /** Real source column names, so the policy editor can classify without anyone typing. */
  const knownColumns = useMemo(() => {
    const map = draft?.field_map ?? {};
    return Object.entries(map)
      .filter(([role, column]) => role !== 'key_strip_prefix' && typeof column === 'string' && column !== '')
      .map(([, column]) => String(column));
  }, [draft?.field_map]);

  // ------------------------------------------------------------------ save

  const save = async (): Promise<void> => {
    if (!saved || !draft || changed.length === 0) return;
    if (keyIsPhone) {
      toast.error('The borrower key cannot be the phone column.', {
        description:
          'The rollup would produce one row per handset, not per account, and a shared number would merge two borrowers.',
      });
      return;
    }

    const body: ClientPatch = {};
    for (const key of changed) {
      // The cast is safe: EDITABLE_FIELDS is a subset of ClientPatch's keys by construction.
      (body as Record<string, unknown>)[key] = draft[key];
    }

    setSaving(true);
    try {
      const written = (await api.clients.patch(clientId, body)) as ClientRow;
      setSaved(written);
      setDraft(written);
      setTtlText(String(written.link_ttl_days));
      setTtlClamped(false);
      toast.success(`${clientId} saved.`, {
        description: `Updated ${changed.map((k) => FIELD_LABEL[k]).join(', ')}.`,
      });
    } catch (err) {
      toast.error(errorMessage(err), { duration: 0 });
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------ render

  if (loading) {
    return (
      <>
        <PageHeader title={clientId || 'Client'} breadcrumbs={[{ label: 'Clients', to: '/clients' }]} />
        <Card>
          <div className="cluster">
            <span className="spinner" aria-hidden="true" />
            <span className="muted">Loading {clientId}…</span>
          </div>
        </Card>
      </>
    );
  }

  if (loadError || !draft || !saved) {
    return (
      <>
        <PageHeader title={clientId || 'Client'} breadcrumbs={[{ label: 'Clients', to: '/clients' }]} />
        <Card>
          <EmptyState
            tone="danger"
            title="Could not load this client"
            description={loadError ?? 'No configuration came back for that id.'}
            action={
              <Button variant="primary" onClick={() => void load()}>
                Retry
              </Button>
            }
            secondaryAction={
              <Link className="btn" to="/clients">
                Back to clients
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  const policy: ColumnPolicy = draft.column_policy ?? {};
  const optInContent = (policy.opt_in ?? []).filter(
    (c) => !isAlwaysNever(c) && callContent(c) !== null,
  ).length;
  const defaultContent = (policy.default ?? []).filter(
    (c) => !isAlwaysNever(c) && callContent(c) !== null,
  ).length;

  const roleColumns: Column<RoleRow>[] = [
    {
      key: 'role',
      header: 'Role',
      width: '230px',
      nowrap: true,
      render: (row) => (
        // One line. `help` moves onto the role name as a ? — it is static documentation,
        // identical on every client, and it used to take the widest column in the table
        // and set the row height by wrapping to two lines.
        <span className="cluster-tight" style={{ minWidth: 0 }}>
          <span className="mono strong truncate" title={`${row.label} — ${row.help}`}>
            {row.role}
          </span>
          <span className="xs dim" aria-hidden="true" title={row.help}>
            ?
          </span>
          {row.custom ? (
            <Badge variant="plain" glyph="◇" title="Not a role this build knows about">
              custom
            </Badge>
          ) : null}
          {row.literal ? (
            <Badge variant="plain" glyph="″" title="A literal value, not a source column name">
              literal
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      // The one variable thing on the row gets the slack.
      key: 'column',
      header: 'Source column',
      render: (row) => (
        <Input
          size="sm"
          mono
          spellCheck={false}
          autoComplete="off"
          aria-label={`${row.role} source column`}
          invalid={
            (row.role === 'key' || row.role === 'phone') && keyIsPhone ? true : undefined
          }
          placeholder={row.literal ? 'literal value' : 'not mapped'}
          value={row.value}
          onChange={(event) => setRole(row.role, event.target.value)}
        />
      ),
    },
    {
      key: 'drives',
      header: 'What it drives',
      width: '230px',
      render: (row) => (
        <div className="xs dim truncate" title={row.help}>
          {row.label}
        </div>
      ),
    },
  ];

  const mappedCount = roleRows.filter((r) => r.value !== '').length;
  // Mapped roles first: a typical client maps 6–12 of 25, and the unmapped ones used to
  // be 13 empty rows scrolled past on the way to the twelve that carry a value.
  const mappedRoles = roleRows.filter((r) => r.value !== '');
  const unmappedRoles = roleRows.filter((r) => r.value === '');
  // With nothing mapped there is nothing to hide, and hiding the unmapped rows would
  // leave an empty table on the one screen where the mapping is done.
  const shownRoles =
    showUnmapped || mappedRoles.length === 0 ? [...mappedRoles, ...unmappedRoles] : mappedRoles;

  const fieldMapOpen = openFieldMap ?? (keyColumn === '' || keyIsPhone);
  const policyOpen =
    openPolicy ??
    (defaultContent > 0 ||
      ((policy.default ?? []).length === 0 &&
        (policy.opt_in ?? []).length === 0 &&
        (policy.never ?? []).length === 0));

  const rankCount = (draft.disposition_ranks ?? []).length;
  /** A count beside each index entry, so the index is also the summary. */
  const sectionCount: Record<string, string> = {
    identity: '',
    links: `${draft.link_ttl_days}d`,
    fieldmap: `${mappedCount}/${roleRows.length}`,
    policy: `${(policy.opt_in ?? []).length} opt-in`,
    ranking: `${rankCount}`,
    recipients: recipientCounts ? `${recipientCounts.deliverableTo} To` : '',
  };
  /** Null when the section is fine; otherwise the sentence the marker explains. */
  const sectionProblem: Record<string, string | null> = {
    identity: keyIsPhone ? 'The borrower key and the phone column are the same column.' : null,
    links:
      draft.link_ttl_days < MAX_LINK_TTL_DAYS
        ? `Links die after ${draft.link_ttl_days} days, not ${MAX_LINK_TTL_DAYS}.`
        : null,
    fieldmap: keyColumn === '' ? 'No borrower key is mapped.' : null,
    policy:
      defaultContent > 0
        ? `${defaultContent} default-tier column(s) carry call content.`
        : null,
    ranking: rankCount === 0 ? 'Nothing is ranked — every borrower resolves to “Not contacted”.' : null,
    recipients:
      recipientCounts && recipientCounts.deliverableTo === 0
        ? 'No deliverable To recipient — a dispatch would reach nobody.'
        : null,
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Clients', to: '/clients' }, { label: clientId }]}
        title={
          <span className="cluster">
            <span>{draft.name || clientId}</span>
            <ActiveBadge active={draft.active} />
            {draft.seeded_only ? (
              <Badge
                tone="info"
                glyph="◇"
                pending
                title="This client lives in config/clients/seed.json only. Saving writes the full row to DynamoDB."
              >
                seed only
              </Badge>
            ) : null}
          </span>
        }
        subtitle={
          <span>
            reports carry <strong>{draft.company_name || 'an unnamed lender'}</strong> borrower
            data
            {/* Provenance on one line under the title — it used to be split between here
                and a kv row 300px lower, and Dispositions put the same pair somewhere
                else again. "Who changed this, and when" is the first question after a
                bad dispatch. */}
            {draft.updated_at ? (
              <span className="dim">
                {' · saved '}
                {istDateTime(draft.updated_at)}
                {draft.updated_by ? ` by ${draft.updated_by}` : ''}
              </span>
            ) : null}
          </span>
        }
        actions={
          <span className="cluster-tight">
            {/* The section index. The page runs ~4,000px and Recipients — the section you
                open it for when an address hard-bounces — is last, behind a 25-row field
                map and an N-row policy table. */}
            {SECTIONS.map((section) => {
              const problem = sectionProblem[section.id];
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="xs"
                  style={{ color: problem ? 'var(--warn)' : undefined }}
                  title={problem ?? undefined}
                >
                  {problem ? '▲ ' : ''}
                  {section.label}
                  {sectionCount[section.id] ? (
                    <span className="dim"> {sectionCount[section.id]}</span>
                  ) : null}
                </a>
              );
            })}
          </span>
        }
      />

      {/* The only Save and Discard on this page, and they follow the operator down it.
          The field map and the column policy both mutate the draft from 1,500–3,000px
          below where the buttons used to sit, and there is no navigation guard anywhere
          in this app — so an edit made down there, then navigated away from, was gone
          with nothing on screen to say so. */}
      {changed.length > 0 ? (
        <div
          className="toolbar"
          role="region"
          aria-label="Unsaved changes"
          style={{
            position: 'sticky',
            top: 'var(--topbar-h)',
            zIndex: 20,
            background: 'var(--warn-soft)',
            borderColor: 'var(--warn-line)',
          }}
        >
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span className="small">
            Unsaved: {changed.map((key) => FIELD_LABEL[key]).join(', ')}. Nothing here reaches a
            dispatch until you save — a dry-run reads the stored configuration, not this draft.
          </span>
          <span className="grow" />
          <Button size="sm" disabled={saving} onClick={() => setDiscarding(true)}>
            Discard
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={keyIsPhone}
            onClick={() => void save()}
          >
            {`Save ${pluralise(changed.length, 'change')}`}
          </Button>
        </div>
      ) : null}

      {keyIsPhone ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ✕
          </span>
          <span>
            The borrower key and the phone column are both <code>{keyColumn}</code>. The rollup
            would then produce one row per handset instead of one per account, and two borrowers
            sharing a number would merge into one. Fix it before saving.
          </span>
        </div>
      ) : null}

      {/* ------------------------------------------------------------- identity */}
      <Card
        className="section-anchor"
        title={
          <span className="cluster-tight" id="identity">
            <span>Identity</span>
            {/* Part days decides whether anything at all can be MAILED for this client —
                it is why a dry run says "nothing may be sent". It was the last footnote
                in a definition list. The beaded border is the console's existing mark for
                "settled, but covers less than it claims". */}
            {draft.allow_partial_window === true ? (
              <Badge
                tone="warn"
                glyph="◧"
                partial
                title="A source that stops before the window closes is still mailed to this client, with its coverage stated in the subject, the body and the report."
              >
                Part days accepted
              </Badge>
            ) : (
              <Badge
                tone="neutral"
                glyph="◧"
                partial
                title="A source that stops before the window closes is not mailed to this client — the run is recorded and nothing goes out, the same way a stale source is handled. An internal download from the Downloads screen still produces the file, labelled with what it actually covers."
              >
                Part days refused
              </Badge>
            )}
          </span>
        }
      >
        <div className="stack">
          {/* The sentence, on the page.

              This explanation used to be visible text and was moved into the badge's
              `title` during the console rework. It decides whether a client is sent a
              part day, which is a client-facing consequence somebody has to be able to
              read, quote in a ticket and screenshot — and a tooltip survives none of
              those, is unreachable by keyboard and does not exist on touch. */}
          <p className="small muted" style={{ margin: 0, maxWidth: '68ch' }}>
            {draft.allow_partial_window === true
              ? 'Part days accepted: a source that stops before the window closes is still mailed to this client, with its coverage stated in the subject, the body and the report.'
              : 'Part days refused: a source that stops before the window closes is not mailed to this client — the run is recorded and nothing goes out, the same way a stale source is handled. An internal download from the Downloads screen still produces the file, labelled with what it actually covers.'}
          </p>

          <FieldRow>
            <Field label="Display name" hint="What the console shows. Free text.">
              {(props) => (
                <Input
                  {...props}
                  value={draft.name}
                  onChange={(event) => patch('name', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="End lender"
              hint="Named on every recipient guard. This is what tells two same-prefix clients apart."
            >
              {(props) => (
                <Input
                  {...props}
                  value={draft.company_name}
                  onChange={(event) => patch('company_name', event.target.value)}
                />
              )}
            </Field>
          </FieldRow>

          <FieldRow>
            <Field
              label="company_id"
              hint="The authoritative workspace key. Not derivable from the S3 prefix — leave it empty only when the client genuinely has none."
            >
              {(props) => (
                <Input
                  {...props}
                  mono
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="not set"
                  value={draft.company_id ?? ''}
                  onChange={(event) => patch('company_id', event.target.value.trim() || null)}
                />
              )}
            </Field>

            <Field
              label="Source prefix"
              hint="Under s3://neuron-report/ — read-only. Must end in a slash."
            >
              {(props) => (
                <Input
                  {...props}
                  mono
                  spellCheck={false}
                  value={draft.s3_prefix}
                  onChange={(event) => patch('s3_prefix', event.target.value.trim())}
                />
              )}
            </Field>
          </FieldRow>

          <div className="cluster">
            <Switch
              checked={draft.active}
              label="Active"
              onChange={(next) => patch('active', next)}
            />
            <span className="xs dim">
              An inactive client keeps its configuration and dispatches nothing.
            </span>
          </div>

          <dl className="kv">
            <dt>Client id</dt>
            <dd className="mono">
              {clientId}{' '}
              <span className="xs dim">
                — permanent. It is the prefix, the filename and every grant's client.
              </span>
            </dd>
            {draft.source_date_format ? (
              <>
                <dt>Source date format</dt>
                <dd className="mono small">{draft.source_date_format}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </Card>

      {/* Known defects sit below Identity: they are context about the SOURCE, not part of
          the client's identity, and a card describing data that is known to be wrong
          should not read quieter than a link-expiry preference two cards down. */}
      {(draft.notes ?? []).length > 0 ? (
        <Card
          title={
            <span className="cluster-tight">
              <span>Known defects in this client&apos;s source export</span>
              <Badge tone="warn" glyph="▲" title="Recorded reasons this client's data is wrong">
                {formatCount((draft.notes ?? []).length)} known defects
              </Badge>
            </span>
          }
        >
          <ul className="stack-tight" style={{ margin: 0, paddingLeft: 'var(--s-4)' }}>
            {(draft.notes ?? []).map((note, index) => (
              <li key={index} className="small">
                {note}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* ------------------------------------------------------------- link life */}
      <Card title={<span id="links">Link life and retention</span>}>
        <div className="stack">
          <FieldRow>
            <Field
              label="Link life (days)"
              hint={
                ttlClamped
                  ? `${MAX_LINK_TTL_DAYS} days is the hard maximum, so that is what will be saved.`
                  : `How long a download link works after dispatch. Hard maximum ${MAX_LINK_TTL_DAYS}.`
              }
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  max={MAX_LINK_TTL_DAYS}
                  value={ttlText}
                  onChange={(event) => {
                    const text = event.target.value;
                    setTtlText(text);
                    if (text.trim() === '') return; // mid-edit; keep the stored value
                    const raw = Math.trunc(Number(text));
                    if (!Number.isFinite(raw)) return;
                    const days = Math.min(Math.max(1, raw), MAX_LINK_TTL_DAYS);
                    setTtlClamped(raw > MAX_LINK_TTL_DAYS);
                    patch('link_ttl_days', days);
                  }}
                  onBlur={() => {
                    // Whatever was typed, the box ends up showing the value that will be saved.
                    setTtlText(String(draft.link_ttl_days));
                    setTtlClamped(false);
                  }}
                />
              )}
            </Field>

            <Field
              label="File retention (days)"
              hint="Blank keeps the generated files indefinitely. Separate from link expiry."
              optional
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  placeholder="keep"
                  value={draft.retention_days ?? ''}
                  onChange={(event) => {
                    const text = event.target.value.trim();
                    if (text === '') {
                      patch('retention_days', null);
                      return;
                    }
                    const days = Math.trunc(Number(text));
                    patch('retention_days', Number.isFinite(days) && days >= 1 ? days : null);
                  }}
                />
              )}
            </Field>
          </FieldRow>

          {/* Invariant for every client and never changing, so one line with the rest
              behind a disclosure. The words are unchanged (CONTRACT §1). The conditional
              `< 7` callout below IS stateful and keeps its box. */}
          <div className="readback">
            <span className="strong">
              Expiry is ours, not S3&apos;s. Expiring a link never deletes a file.
            </span>{' '}
            <details style={{ display: 'inline' }}>
              <summary className="xs dim" style={{ cursor: 'pointer', display: 'inline' }}>
                How the window is enforced
              </summary>
              <span>
                {' '}
                The emailed link carries an opaque token; the resolver checks our{' '}
                <code>expires_at</code> and only then mints a 60-second presigned URL. So the
                window is exact, it survives credential rotation, and a grant can be revoked
                instantly. Retention is the setting beside it, and it is a different question.
              </span>
            </details>
          </div>

          {draft.link_ttl_days < MAX_LINK_TTL_DAYS ? (
            <div className="callout tone-warn">
              <span className="callout-glyph" aria-hidden="true">
                ▲
              </span>
              <span>
                Links for this client die after {draft.link_ttl_days}{' '}
                {draft.link_ttl_days === 1 ? 'day' : 'days'}, not {MAX_LINK_TTL_DAYS}. A recipient
                who opens the mail the following week gets a 410 page. Re-issuing from the run
                mints a new token; it does not extend the old one.
              </span>
            </div>
          ) : null}
        </div>
      </Card>

      {/* ------------------------------------------------------------- field map */}
      <Card
        title={<span id="fieldmap">Field map</span>}
        actions={
          <>
            <span className="xs dim">
              {formatCount(mappedCount)} of {formatCount(roleRows.length)} roles mapped
            </span>
            {keyColumn === '' ? (
              <Badge tone="warn" glyph="▲" title="A borrower-shape report has nothing to group on">
                no borrower key
              </Badge>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setOpenFieldMap(!fieldMapOpen)}>
              {fieldMapOpen ? 'Hide' : 'Show'}
            </Button>
          </>
        }
        flush
        footer={
          <div className="cluster">
            <label className="xs dim" htmlFor="nrd-role-add">
              Add a custom role
            </label>
            <Input
              id="nrd-role-add"
              size="sm"
              mono
              spellCheck={false}
              autoComplete="off"
              placeholder="role name"
              value={newRole}
              onChange={(event) => setNewRole(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addRole();
                }
              }}
              style={{ maxWidth: 200 }}
            />
            <Button size="sm" disabled={!newRole.trim()} onClick={addRole}>
              Add role
            </Button>
            <span className="grow" />
            {unmappedRoles.length > 0 ? (
              <Button size="sm" variant="ghost" onClick={() => setShowUnmapped((v) => !v)}>
                {showUnmapped
                  ? `Hide ${formatCount(unmappedRoles.length)} unmapped roles`
                  : `+${formatCount(unmappedRoles.length)} unmapped roles`}
              </Button>
            ) : null}
            <span className="xs dim">
              Blank a column to unmap the role. Missing roles are legal — a client simply lacks
              that concept.
            </span>
          </div>
        }
      >
        {fieldMapOpen ? (
          <Table
            columns={roleColumns}
            rows={shownRoles}
            rowKey={(row) => row.role}
            dense
            flush
            caption="Logical role to source column (CONTRACT §4)"
            rowTone={(row) =>
              (row.role === 'key' || row.role === 'phone') && keyIsPhone
                ? 'danger'
                : row.role === 'key' && row.value === ''
                  ? 'warn'
                  : 'default'
            }
          />
        ) : (
          <p className="small dim" style={{ padding: 'var(--s-3)', margin: 0 }}>
            {formatCount(mappedCount)} of {formatCount(roleRows.length)} roles mapped. A missing
            role is legal — a client simply lacks that concept.
          </p>
        )}
      </Card>

      {keyColumn === '' ? (
        <div className="callout tone-warn">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            No borrower key is mapped. A borrower-shape report has nothing to group on, so the
            rollup cannot produce one row per account for this client.
          </span>
        </div>
      ) : null}

      {/* ------------------------------------------------------------- column policy */}
      <Card
        title={<span id="policy">Column policy</span>}
        actions={
          <>
            <span className="cluster-tight xs dim">
              <PolicyBadge tier="opt_in" /> {formatCount(optInContent)} carry call content
              {defaultContent > 0 ? (
                <Badge tone="danger" glyph="✕" terminalBad title="Call content in the default tier">
                  {formatCount(defaultContent)} shipping by default
                </Badge>
              ) : null}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setOpenPolicy(!policyOpen)}>
              {policyOpen ? 'Hide' : 'Show'}
            </Button>
          </>
        }
      >
        {policyOpen ? (
          <ColumnPolicyEditor
            value={policy}
            onChange={(next) => patch('column_policy', next)}
            knownColumns={knownColumns}
            companyName={draft.company_name}
          />
        ) : (
          <p className="small dim" style={{ margin: 0 }}>
            {formatCount((policy.default ?? []).length)} default ·{' '}
            {formatCount((policy.opt_in ?? []).length)} opt-in ·{' '}
            {formatCount((policy.never ?? []).length)} never. Opt-in columns ship only when a
            schedule lists them (CONTRACT §6).
          </p>
        )}
      </Card>

      {/* ------------------------------------------------------------- dispositions */}
      <Card
        title={<span id="ranking">Disposition ranking</span>}
        actions={
          <Link className="btn btn-sm" to={`/dispositions/${encodeURIComponent(clientId)}`}>
            Open ranking
          </Link>
        }
      >
        <div className="stack">
          <dl className="kv">
            <dt>Ranked outcomes</dt>
            <dd>
              {(draft.disposition_ranks ?? []).length === 0 ? (
                <span className="dim">None ranked — every borrower would come back “Not contacted”.</span>
              ) : (
                <span className="cluster-tight">
                  {(draft.disposition_ranks ?? []).map((value, index) => (
                    <Badge key={value} tone={index === 0 ? 'ok' : 'neutral'} glyph={`${index + 1}`}>
                      <span className="mono">{value}</span>
                    </Badge>
                  ))}
                </span>
              )}
            </dd>
            <dt>Declared non-contact</dt>
            <dd>
              {(draft.non_contact ?? []).length === 0 ? (
                <span className="dim">None declared.</span>
              ) : (
                <span className="cluster-tight">
                  {(draft.non_contact ?? []).map((value) => (
                    <Badge key={value} tone="muted" glyph="○">
                      <span className="mono">{value}</span>
                    </Badge>
                  ))}
                </span>
              )}
            </dd>
            {/* CONTRACT-FLOWS §1: the ladder belongs to the flow when a client runs several
                campaigns under one prefix. Shown here so the ranking above is not read as
                the whole story; edited on the ranking screen. */}
            <dt>Flows</dt>
            <dd>
              {Object.keys(draft.flows ?? {}).length === 0 ? (
                <span className="dim">
                  One ranking for every campaign — the ranking above applies to every row.
                </span>
              ) : (
                <span className="cluster-tight">
                  {Object.entries(draft.flows ?? {}).map(([name, entry]) =>
                    entry?.ranking === 'none' ? (
                      <Badge
                        key={name}
                        tone="neutral"
                        glyph="—"
                        title={entry.reason || 'No best outcome exists in this flow'}
                      >
                        <span className="mono">{name}</span> · no best outcome
                      </Badge>
                    ) : (
                      <Badge
                        key={name}
                        tone="info"
                        glyph="▸"
                        title={
                          (entry?.disposition_ranks ?? []).length > 0
                            ? `${(entry?.disposition_ranks ?? []).length} ranked outcomes of its own`
                            : 'Inherits the client ranking'
                        }
                      >
                        <span className="mono">{name}</span>
                      </Badge>
                    ),
                  )}
                </span>
              )}
            </dd>
          </dl>
          <p className="small dim">
            Ranking is edited on its own screen because it is a commercial decision, not a
            configuration detail: it decides which call a borrower's report row is built from.
          </p>
        </div>
      </Card>

      {/* ------------------------------------------------------------- recipients */}
      <Card
        title={<span id="recipients">Recipients</span>}
        actions={
          <>
            {recipientCounts ? (
              // One counts line, in the head, carrying all three numbers — the editor's
              // own footer printed the same three in a different order 500px below.
              <span className="xs dim">
                {formatCount(recipientCounts.total)} configured ·{' '}
                {formatCount(recipientCounts.active)} active ·{' '}
                {formatCount(recipientCounts.deliverableTo)} deliverable To
              </span>
            ) : null}
            {/* This is how someone toggles a recipient off, discards the page, and
                believes they are still active. It belongs where it is read, not as the
                tail of a dim sentence under the table. */}
            <Badge
              tone="info"
              glyph="↥"
              title="Recipient edits are written immediately. They are not part of the client draft and Discard does not undo them."
            >
              Saves immediately
            </Badge>
          </>
        }
      >
        <RecipientEditor
          clientId={clientId}
          clientName={draft.name}
          companyName={draft.company_name}
          onCountChange={onRecipientCounts}
        />
      </Card>

      <ConfirmDialog
        open={discarding}
        tone="warn"
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        consequence={`${changed.map((key) => FIELD_LABEL[key]).join(', ')} will go back to what is stored.`}
        onCancel={() => setDiscarding(false)}
        onConfirm={() => {
          setDraft(saved);
          setTtlText(String(saved.link_ttl_days));
          setTtlClamped(false);
          setDiscarding(false);
        }}
      >
        <p className="small">Recipient changes are already saved and are not affected.</p>
      </ConfirmDialog>
    </>
  );
}
